import React, { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';
import { FREE_PLAN, SOLO_PLANS } from '@/content/facts';
import { useAppleIAP } from '@/hooks/useAppleIAP';
import api from '@/services/api';
import { INTRO_OFFER } from '@/services/introOffer';
import { fetchStorePlanPrices } from '@/services/storePrices';
import { useToastStore } from '@/store/toastStore';

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const brl = (value: number) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const INTRO_PRICE = brl(INTRO_OFFER.price);
const PRO_WEB_PRICE = SOLO_PLANS.find((plan) => plan.id === INTRO_OFFER.planId)!.webPrice;

interface IntroOfferStepProps {
  /** Rótulo da saída sem assinar (muda quando o recebimento vem em seguida). */
  freeLabel: string;
  /** Seguiu no gratuito ou terminou a compra. */
  onDone: () => void;
}

/**
 * Último passo do onboarding: o 1º mês do Pro por R$ 1,99 em destaque, o que o
 * gratuito não tem logo abaixo, e os planos para quem prefere assinar direto.
 * Seguir no gratuito pede confirmação, repetindo o que fica de fora.
 */
export function IntroOfferStep({ freeLabel, onDone }: IntroOfferStepProps) {
  const { addToast } = useToastStore();
  const [confirmFree, setConfirmFree] = useState(false);
  const [startingWeb, setStartingWeb] = useState(false);

  const iap = useAppleIAP({
    onSuccess: () => {
      addToast('Pronto! Seu mês do Pro começou.', 'success');
      onDone();
    },
    onError: (message) => addToast(message, 'error'),
  });
  const isStore = Platform.OS === 'ios' || Platform.OS === 'android';
  const purchasing = iap.state === 'purchasing' || iap.state === 'validating';

  // Na loja, o preço depois do teste é o da tabela dela.
  const { data: storePrices } = useQuery({
    queryKey: ['store-plan-prices', Platform.OS, INTRO_OFFER.planId],
    queryFn: () => fetchStorePlanPrices([INTRO_OFFER.planId]),
    enabled: isStore,
    staleTime: 60 * 60 * 1000,
  });
  const proPrice = brl(storePrices?.[INTRO_OFFER.planId] ?? PRO_WEB_PRICE);

  const startTrial = async () => {
    if (isStore) {
      await iap.purchaseSubscription(INTRO_OFFER.planId);
      return;
    }
    // Web: o checkout do Pro; a API aplica o desconto do 1º mês.
    setStartingWeb(true);
    try {
      const res = await api.get<{ id: string; priceId: string | null }[]>('/stripe/plans');
      const pro = res.data.find((plan) => plan.id === INTRO_OFFER.planId);
      if (!pro?.priceId) throw new Error('sem preço');
      router.push({
        pathname: '/subscription/checkout',
        params: { planId: pro.id, priceId: pro.priceId },
      });
    } catch {
      addToast('Não foi possível abrir a assinatura. Tente novamente.', 'error');
    } finally {
      setStartingWeb(false);
    }
  };

  const busy = purchasing || startingWeb;

  return (
    <>
      <KeyboardAwareScrollView
        bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        contentContainerClassName="px-6 py-6 gap-5"
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-1">
          <ThemedText className="font-bold text-2xl text-foreground">
            Teste tudo por R$ {INTRO_PRICE}
          </ThemedText>
          <ThemedText className="text-sm leading-5 text-foreground/55">
            1 mês com o plano Pro. Depois, só continua se fizer sentido pra você.
          </ThemedText>
        </View>

        <View className="gap-1 rounded-2xl bg-primary p-5" style={{ borderCurve: 'continuous' }}>
          <ThemedText className="text-xs text-white/85">1 mês do plano Pro</ThemedText>
          <ThemedText className="font-bold text-4xl text-white">R$ {INTRO_PRICE}</ThemedText>
          <ThemedText className="text-xs text-white/85">
            Depois R$ {proPrice}/mês · cancele quando quiser
          </ThemedText>
        </View>

        <View className="gap-2 rounded-2xl border border-border/40 bg-card p-4">
          <ThemedText className="font-semibold text-[15px] text-foreground">
            O gratuito não tem
          </ThemedText>
          <MissingList />
          <ThemedText className="text-xs text-foreground/55">
            E fica limitado a {FREE_PLAN.maxClientsPerMonth} clientes por mês.
          </ThemedText>
        </View>

        <Pressable
          onPress={() => router.push('/subscription/plans')}
          hitSlop={8}
          className="self-center"
        >
          <ThemedText className="font-semibold text-sm text-foreground/70 underline">
            Prefiro já assinar um plano
          </ThemedText>
        </Pressable>
      </KeyboardAwareScrollView>

      <View className="gap-2 px-6 pb-4 pt-2">
        <Button onPress={() => void startTrial()} disabled={busy} className="w-full">
          {busy ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <ThemedText className="font-semibold text-white">
              Começar teste por R$ {INTRO_PRICE}
            </ThemedText>
          )}
        </Button>
        <Button
          variant="ghost"
          onPress={() => setConfirmFree(true)}
          disabled={busy}
          className="w-full"
        >
          <ThemedText className="font-semibold text-foreground/70">{freeLabel}</ThemedText>
        </Button>
      </View>

      <Dialog open={confirmFree} onOpenChange={setConfirmFree}>
        <DialogContent className="gap-4 rounded-2xl" style={{ borderCurve: 'continuous' }}>
          <ThemedText className="font-bold text-lg text-neutral-900">
            Seguir no gratuito?
          </ThemedText>
          <View className="gap-2">
            <ThemedText className="text-sm text-neutral-600">No gratuito você fica sem:</ThemedText>
            <MissingList />
            <ThemedText className="text-sm text-foreground/70">
              E com até {FREE_PLAN.maxClientsPerMonth} clientes por mês.
            </ThemedText>
          </View>
          <ThemedText className="text-sm text-neutral-600">
            Por R$ {INTRO_PRICE} você testa tudo isso por 1 mês.
          </ThemedText>
          <View className="gap-2">
            <Button
              title={`Testar por R$ ${INTRO_PRICE}`}
              onPress={() => {
                setConfirmFree(false);
                void startTrial();
              }}
            />
            <Button
              title="Sim, seguir no gratuito"
              variant="ghost"
              onPress={() => {
                setConfirmFree(false);
                onDone();
              }}
            />
          </View>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MissingList() {
  return (
    <View className="gap-2">
      {FREE_PLAN.paidOnly.map((item) => (
        <View key={item} className="flex-row items-center gap-3">
          <Ionicons name="close-circle-outline" size={16} className="text-neutral-400" />
          <ThemedText className="flex-1 text-sm text-foreground/70">{capitalize(item)}</ThemedText>
        </View>
      ))}
    </View>
  );
}
